import {
	summarizeOrFormatCost,
	summarizeOrFormatUnits,
} from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/Holdings/holdings.utils.ts';
import { WideViewOwnershipTableItemData } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/Ownership/WideViewOwnershipTable/useWideViewOwnershipTableController';
import { isSecurityIssuedInCurrency } from '@dealstack/frontend/shared/cap.ts';
import { Icon } from '@dealstack/frontend/shared/components/fundament/Icon/Icon';
import { NumericCell } from '@dealstack/frontend/shared/components/Table/MantineTable/Cells/NumericCell';
import { createNumericHeader } from '@dealstack/frontend/shared/components/Table/MantineTable/ColumnHeader/ColumnHeader';
import { ExtendedColumnDef } from '@dealstack/frontend/shared/components/Table/MantineTable/useMantineTableWithPaginationFilteringSorting';
import { defaultTheme } from '@dealstack/frontend/shared/mechanism/styleguide/defaultTheme';
import { LONG_DASH } from '@dealstack/frontend/shared/other/react/specialSigns';
import { I18Namespaces } from '@dealstack/shared/lib/common/i18n/i18n.ts';
import { numberToUiPercent } from '@dealstack/shared/lib/format/number';
import { DataSecurityTct, SecurityIssuedIn } from '@dealstack/shared/lib/generated';
import {
	GENERIC_ELEMENT_ID,
	makeComplexDataTestId,
} from '@dealstack/shared/tests/testElementIds.ts';
import { Flex, MantineTheme, rem, Text, TextInput } from '@mantine/core';
import { Row } from '@tanstack/react-table';
import React from 'react';
import { TranslateFunction } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.types';

type WideViewOwnershipTableColumn = ExtendedColumnDef<WideViewOwnershipTableItemData>;
const INDENTATION_PER_LEVEL_PX = 16;
interface Props {
	value: string;
	row: Row<WideViewOwnershipTableItemData>;
	table: unknown;
}

const ExpandableCell: React.FC<Props> = ({ value, row }) => {
	const canExpand = row.getCanExpand();
	const isExpanded = row.getIsExpanded();
	const depth = row.depth;

	const handleClick = () => {
		if (canExpand) {
			row.toggleExpanded();
		}
	};

	return (
		<Flex
			align="center"
			gap="xs"
			style={{
				cursor: canExpand ? 'pointer' : 'default',
				width: '100%',
				minWidth: 0,
				paddingLeft: rem(depth * INDENTATION_PER_LEVEL_PX),
			}}
			onClick={handleClick}
		>
			<Icon
				style={{ visibility: canExpand ? 'visible' : 'hidden' }}
				variant={isExpanded ? 'caret-down' : 'caret-right'}
				size="lg"
				color={defaultTheme.v3.colors.black[0]}
			/>
			<Text
				size="B6Medium"
				style={{
					overflow: 'hidden',
					textOverflow: 'ellipsis',
					whiteSpace: 'nowrap',
					flex: 1,
				}}
			>
				{value}
			</Text>
		</Flex>
	);
};

export const getWideViewOwnershipTableColumns = (
	securities: DataSecurityTct[],
	tCapManage: TranslateFunction<I18Namespaces.CAP_MANAGE>,
	showSummarized: boolean,
	showPercentage: boolean,
	theme?: MantineTheme
): WideViewOwnershipTableColumn[] => [
	{
		header: tCapManage('holdings.table.columns.holder'),
		columnLabel: tCapManage('holdings.table.columns.holder'),
		id: 'holder',
		accessorFn: (row) => {
			const baseName = row.holderName || '';
			const heldForDisplay = row.heldForDisplayName ? `(for ${row.heldForDisplayName})` : '';

			if (row.subRows) {
				return baseName;
			}

			return `${baseName} (${row.mipName}) ${heldForDisplay}`;
		},
		sortingFn: (rowA, rowB, columnId) => {
			const a = rowA.getValue<string>(columnId) ?? '';
			const b = rowB.getValue<string>(columnId) ?? '';
			return a.toLowerCase().localeCompare(b.toLowerCase());
		},
		cell: ({ getValue, row, table }) => (
			<ExpandableCell value={getValue<string>()} row={row} table={table} />
		),
		minSize: 300,
		filterText: tCapManage('holdings.table.filter.holder'),
		showFilter: true,
		filter: ({ column }) => (
			<TextInput
				value={(column.getFilterValue() as string) ?? ''}
				placeholder={tCapManage('holdings.table.filter.holder')}
				onChange={(e) => column.setFilterValue(e.currentTarget.value)}
				size="sm"
				styles={{
					input: {
						borderColor: `1px solid ${defaultTheme.v3.colors.gray[4]}`,
						'&:hover': {
							borderColor: `${defaultTheme.v3.colors.dark[0]}`,
						},
						'&:focus': {
							borderColor: `${defaultTheme.v3.colors.dark[0]}`,
						},
						'&:focus-within': {
							borderColor: `${defaultTheme.v3.colors.dark[0]}`,
						},
					},
				}}
			/>
		),
	},
	...securities.map((security) => {
		const mappings = getColumnMappingByIssuedIn(security.issuedIn);

		let columnDefinitions = mappings.map((mapping) =>
			createColumnDefinition(
				mapping,
				security,
				tCapManage,
				showSummarized,
				showPercentage,
				theme
			)
		);

		if (columnDefinitions.length === 0) {
			columnDefinitions = [
				createColumnDefinition(
					'no-holding',
					security,
					tCapManage,
					showSummarized,
					showPercentage
				),
			];
		}

		return {
			header: security.name,
			columnLabel: security.name,
			id: security.id,
			columns: columnDefinitions.filter(Boolean),
		};
	}),
];

const createColumnDefinition = (
	mapping: string,
	security: DataSecurityTct,
	tCapManage: TranslateFunction<I18Namespaces.CAP_MANAGE>,
	showSummarized: boolean,
	percentage: boolean,
	_theme?: MantineTheme
): WideViewOwnershipTableColumn => {
	const formatValue = (value: number, isPercentage: boolean, isSummarized: boolean) => {
		if (!value || isNaN(value) || value === 0) {
			return LONG_DASH;
		}

		if (isPercentage) {
			return numberToUiPercent(value) ?? LONG_DASH;
		}

		return summarizeOrFormatUnits(value, security.unitDenominator, isSummarized);
	};

	const formatPercentageHeader = (header: string, isPercentage: boolean) =>
		isPercentage ? `${header} (%)` : header;
	const currency = security.currency;

	const mapConfig: Record<
		string,
		{
			header: string;
			columnLabel: string;
			accessorFn: (
				row: WideViewOwnershipTableItemData
			) => { value: number | null; dataTestId: string } | number | null;
			cell: (data: { value: number | null; dataTestId: string }) => React.ReactNode;
			sortingFn?: (
				rowA: Row<WideViewOwnershipTableItemData>,
				rowB: Row<WideViewOwnershipTableItemData>,
				columnId: string
			) => number;
			filterText?: string;
			rightAligned?: boolean;
			isDisabled?: boolean;
			minSize?: number;
		}
	> = {
		'shareHolding.unitsAmountPercentage': {
			header: formatPercentageHeader(
				tCapManage('holdings.table.columns.unit_amount'),
				percentage
			),
			columnLabel: formatPercentageHeader(
				tCapManage('holdings.table.columns.unit_amount'),
				percentage
			),
			accessorFn: (row: WideViewOwnershipTableItemData) => {
				const foundSecurity = row.securities.find((s) => s.securityId === security.id);
				const dataTestId = makeComplexDataTestId(
					GENERIC_ELEMENT_ID.MANTINE_TABLE_CELL,
					foundSecurity?.name
				);
				if (!foundSecurity) {
					return { value: null, dataTestId };
				}

				if (percentage) {
					return {
						value: foundSecurity.shareHolding.unitsPercentage ?? null,
						dataTestId,
					};
				}

				if (security.issuedIn === SecurityIssuedIn.CURRENCY) {
					return { value: foundSecurity.shareHolding.cost ?? null, dataTestId };
				}

				if (security.issuedIn === SecurityIssuedIn.UNITS) {
					return { value: foundSecurity.shareHolding.unitsAmount ?? null, dataTestId };
				}

				return { value: null, dataTestId };
			},
			cell: (data: { value: number | null; dataTestId: string }) => {
				const { value, dataTestId } = data;

				if (value === null || value === undefined) {
					return <NumericCell value={LONG_DASH} dataTestId={dataTestId} />;
				}

				if (percentage) {
					const displayValue = (value && numberToUiPercent(value)) || LONG_DASH;
					return <NumericCell value={displayValue} dataTestId={dataTestId} />;
				}

				if (security.issuedIn === SecurityIssuedIn.CURRENCY) {
					const currencyDisplay = security.currency
						? `${security.currency} ${summarizeOrFormatCost(value, showSummarized)}`
						: summarizeOrFormatCost(value, showSummarized);
					return <NumericCell value={currencyDisplay} dataTestId={dataTestId} />;
				}

				if (security.issuedIn === SecurityIssuedIn.UNITS) {
					const displayValue = formatValue(value, percentage, showSummarized);
					return <NumericCell value={displayValue} dataTestId={dataTestId} />;
				}

				return <NumericCell value={LONG_DASH} dataTestId={dataTestId} />;
			},
			sortingFn: (rowA, rowB, columnId) =>
				((rowA.getValue(columnId) as number) ?? 0) -
				((rowB.getValue(columnId) as number) ?? 0),
			rightAligned: true,
			filterText: tCapManage('holdings.table.filter.unit_amount'),
			minSize: 160,
		},
		'shareHolding.votesAmountPercentage': {
			header: formatPercentageHeader(
				tCapManage('holdings.table.columns.votes_amount'),
				percentage
			),
			columnLabel: formatPercentageHeader(
				tCapManage('holdings.table.columns.votes_amount'),
				percentage
			),
			accessorFn: (row: WideViewOwnershipTableItemData) => {
				const foundSecurity = row.securities.find((s) => s.securityId === security.id);
				const dataTestId = makeComplexDataTestId(
					GENERIC_ELEMENT_ID.MANTINE_TABLE_CELL,
					foundSecurity?.name
				);
				if (!foundSecurity) {
					return { value: null, dataTestId };
				}

				const result = percentage
					? foundSecurity.shareHolding.votesPercentage
					: foundSecurity.shareHolding.votesAmount;

				return {
					value: typeof result === 'number' ? result : null,
					dataTestId,
				};
			},
			cell: (data: { value: number | null; dataTestId: string }) => (
				<NumericCell value={formatValue(data.value ?? 0, percentage, showSummarized)} />
			),
			sortingFn: (rowA, rowB, columnId) =>
				((rowA.getValue(columnId) as number) ?? 0) -
				((rowB.getValue(columnId) as number) ?? 0),
			rightAligned: true,
			filterText: tCapManage('holdings.table.filter.votes_amount'),
			minSize: 160,
		},
		'shareHolding.cost': {
			header: `${tCapManage('holdings.table.columns.cost')} (${currency})`,
			columnLabel: `${tCapManage('holdings.table.columns.cost')} (${currency})`,
			accessorFn: (row: WideViewOwnershipTableItemData) => {
				const foundSecurity = row.securities.find((s) => s.securityId === security.id);
				const dataTestId = makeComplexDataTestId(
					GENERIC_ELEMENT_ID.MANTINE_TABLE_CELL,
					foundSecurity?.name
				);
				if (!foundSecurity) {
					return { value: null, dataTestId };
				}

				const result = foundSecurity.shareHolding?.cost;

				return {
					value: typeof result === 'number' ? result : null,
					dataTestId,
				};
			},
			cell: (data: { value: number | null; dataTestId: string }) => (
				<NumericCell value={summarizeOrFormatCost(data.value ?? 0, showSummarized)} />
			),
			sortingFn: (rowA, rowB, columnId) =>
				((rowA.getValue(columnId) as number) ?? 0) -
				((rowB.getValue(columnId) as number) ?? 0),
			rightAligned: true,
			isDisabled: percentage,
			filterText: tCapManage('holdings.table.filter.cost'),
			minSize: 160,
		},
		name: {
			header: tCapManage('holdings.table.columns.holder'),
			columnLabel: tCapManage('holdings.table.columns.holder'),
			accessorFn: () => null,
			cell: () => null,
			filterText: tCapManage('holdings.table.filter.holder'),
			minSize: 160,
		},
		type: {
			header: tCapManage('holdings.table.columns.type'),
			columnLabel: tCapManage('holdings.table.columns.type'),
			accessorFn: () => null,
			cell: () => null,
			filterText: tCapManage('holdings.table.filter.type'),
		},
	};

	const config = mapConfig[mapping];
	if (!config) {
		const fallbackHeader = mapping.split('.').pop() || '';
		return {
			header: fallbackHeader,
			columnLabel: fallbackHeader,
			id: `${security.id}-${mapping}`,
			accessorFn: () => null,
			cell: () => <Text size="B6Medium">{LONG_DASH}</Text>,
			minSize: 160,
		};
	}

	return {
		header: config.rightAligned ? createNumericHeader(config.header) : config.header,
		columnLabel: config.columnLabel,
		id: `${security.id}-${mapping}`,
		accessorFn: config.accessorFn,
		cell: ({ getValue }) =>
			config.cell(getValue() as { value: number | null; dataTestId: string }),
		sortingFn: config.sortingFn,
		minSize: 160,
		meta: {},
		filterText: config.filterText,
		showFilter: !!config.filterText,
		filter: config.filterText
			? ({ column }) => (
					<TextInput
						value={(column.getFilterValue() as string) ?? ''}
						placeholder={config.filterText}
						onChange={(e) => column.setFilterValue(e.currentTarget.value)}
						size="sm"
						styles={{
							input: {
								borderColor: `1px solid ${defaultTheme.v3.colors.gray[4]}`,
								'&:hover': {
									borderColor: `${defaultTheme.v3.colors.dark[0]}`,
								},
								'&:focus': {
									borderColor: `${defaultTheme.v3.colors.dark[0]}`,
								},
								'&:focus-within': {
									borderColor: `${defaultTheme.v3.colors.dark[0]}`,
								},
							},
						}}
					/>
				)
			: undefined,
		filterFn: (row, columnId, filterValue) => {
			const value: { value: string } = row.getValue(columnId);
			return `${value?.value}`.toLowerCase().includes(`${filterValue}`.toLowerCase());
		},
	};
};

const getColumnMappingByIssuedIn = (issuedIn: SecurityIssuedIn): string[] => {
	return isSecurityIssuedInCurrency(issuedIn)
		? ['shareHolding.cost']
		: [
				'shareHolding.unitsAmountPercentage',
				'shareHolding.votesAmountPercentage',
				'shareHolding.cost',
			];
};
